import type { BlocoDaOperacao, Escrita, EtapaDoBloco, OperacaoDoDia, SetorDoGiro } from './giro';

/**
 * Regras das telas do GESTOR do Giro que não dependem do React (caça a bugs 26/09, itens 26–29).
 * Ficam aqui, puras, para o teste provar sem navegador.
 */

/**
 * BLOCOS FIXOS, BALCONISTAS TROCAM (dono, 27/09/2026): o gestor só muda a pessoa de bloco enquanto
 * o bloco NÃO COMEÇOU. Começou (contagem, reposição…), não transfere — a MESMA regra do servidor
 * (`transferirBloco`).
 */
const ETAPAS_TRANSFERIVEIS: readonly EtapaDoBloco[] = ['AGUARDANDO_RESPONSAVEL', 'NAO_INICIADO'];

export function podeTransferirBloco(
  b: Pick<BlocoDaOperacao, 'missaoId' | 'total' | 'etapa'>,
): boolean {
  return !!b.missaoId && b.total > 0 && ETAPAS_TRANSFERIVEIS.includes(b.etapa);
}

/**
 * Soltar/escolher a pessoa X num bloco B: se X já tem bloco nesta operação, é TROCA (X ↔ dono de B)
 * — devolve o bloco de X. `undefined` = X não tem bloco aqui (o bloco só passa para X).
 * `null` = não dá: X tem bloco começado (ou mais de um) — o servidor recusaria.
 */
export function blocoDaTroca<
  T extends Pick<BlocoDaOperacao, 'numero' | 'missaoId' | 'total' | 'etapa' | 'responsavel'>,
>(
  b: Pick<BlocoDaOperacao, 'numero'>,
  accountId: string,
  blocos: readonly T[],
): T | null | undefined {
  const dela = blocos.filter(
    (x) =>
      x.numero !== b.numero &&
      !!x.missaoId &&
      x.responsavel?.accountId === accountId &&
      x.etapa !== 'CANCELADO',
  );
  if (!dela.length) return undefined;
  const [unico] = dela;
  if (dela.length > 1 || !unico || !podeTransferirBloco(unico)) return null;
  return unico;
}

/**
 * Os setores que a "Nova geração" pode simular/gerar AGORA: marcados pelo gestor, ativos, que geram
 * e sem operação viva no dia (rascunho ou publicada). Seleção antiga de um setor que ganhou
 * rascunho depois fica de fora — a caixa aparece desmarcada, então não pode valer escondida.
 */
export function setoresParaGerar(
  marcados: ReadonlySet<string>,
  setores: readonly Pick<SetorDoGiro, 'id' | 'ativo' | 'gera'>[],
  op: Pick<OperacaoDoDia, 'setores'> | null,
): string[] {
  return setores
    .filter((s) => s.ativo && s.gera && marcados.has(s.id))
    .filter((s) => {
      const g = op?.setores.find((x) => x.setorId === s.id)?.geracao;
      return !g || g.estado === 'CANCELADA';
    })
    .map((s) => s.id);
}

/** Quem o editor de setores está mostrando: um setor salvo (`sel`) ou o formulário de um novo. */
export interface SelecaoDoEditor {
  readonly sel: string | null;
  readonly novo: boolean;
}

/**
 * Depois de salvar, o editor passa a mostrar o setor SALVO (o id que o servidor devolveu). Ficar em
 * "novo" fazia o segundo "Salvar" criar outro setor igual.
 */
export const selecaoAposSalvar = (idSalvo: string): SelecaoDoEditor => ({
  sel: idSalvo,
  novo: false,
});

/**
 * O DIA (AAAA-MM-DD) de um instante, em Brasília. Cortar o ISO (`slice(0, 10)`) dá o dia em UTC:
 * um ciclo iniciado às 22h de 27/09 aparecia como 28/09.
 */
export const diaDeBrasilia = (iso: string): string =>
  new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

/**
 * QUADRO DE BLOCOS (Setores & blocos): soltar a pessoa num bloco = passar o bloco para ela — ou
 * TROCAR, se ela já tem bloco aqui. Só com os blocos NÃO COMEÇADOS (os dois, na troca), nunca para
 * quem já é o dono nem para quem está de folga hoje — o servidor recusaria, e a tela não deve nem
 * acender.
 */
export function podeSoltarNoBloco(
  b: Pick<BlocoDaOperacao, 'numero' | 'missaoId' | 'total' | 'etapa' | 'responsavel'>,
  pessoa: { readonly accountId: string; readonly presenca: string } | null,
  blocos: readonly Pick<
    BlocoDaOperacao,
    'numero' | 'missaoId' | 'total' | 'etapa' | 'responsavel'
  >[] = [],
): boolean {
  if (!pessoa || pessoa.presenca === 'AUSENTE') return false;
  if (b.responsavel?.accountId === pessoa.accountId) return false;
  if (!podeTransferirBloco(b)) return false;
  return blocoDaTroca(b, pessoa.accountId, blocos) !== null;
}

/**
 * Cancela as operações do dia UMA A UMA e PARA no primeiro erro (U4, revisão de 27/09). Seguir às
 * cegas deixava o dia meio cancelado, cada erro apagando o anterior, sem dizer quais foram. Devolve
 * quantas foram, o total e — se parou — o que falta (repetir não recancela as já canceladas) e o
 * porquê, em pt-BR, com o nome do setor.
 */
export async function cancelarEmSequencia(
  alvos: readonly { readonly id: string; readonly nome: string }[],
  cancelarUm: (id: string) => Promise<Escrita<unknown>>,
): Promise<{
  readonly feitas: number;
  readonly total: number;
  readonly faltam: readonly { readonly id: string; readonly nome: string }[];
  readonly erro: string | null;
}> {
  let feitas = 0;
  for (const a of alvos) {
    const r = await cancelarUm(a.id);
    if (r.tipo === 'confirmada') {
      feitas += 1;
      continue;
    }
    const porque =
      r.tipo === 'recusada'
        ? r.mensagem
        : 'sem resposta do servidor — pode ter sido cancelada. Recarregue antes de repetir.';
    return {
      feitas,
      total: alvos.length,
      faltam: alvos.slice(feitas),
      erro: `${String(feitas)} de ${String(alvos.length)} canceladas. Parou em ${a.nome}: ${porque}`,
    };
  }
  return { feitas, total: alvos.length, faltam: [], erro: null };
}
